import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";

const FLAG_LABELS = {
  same_device: "Signed up on a device the inviting shop uses",
  same_owner: "Both shops have the same owner",
};

const STATUSES = [
  ["qualified", "Waiting for you"],
  ["signed_up", "Not paid yet"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
  ["all", "All"],
];

/**
 * Shops that invited shops. An invitation *qualifies* by itself once both shops
 * have paid a month; approving it gives each shop one free month (banked for one
 * that is not active, added at its next payment) and emails both owners. Rejecting
 * needs a reason. What looks wrong was flagged at signup.
 */
export default function Referrals() {
  const [status, setStatus] = useState("qualified");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await platformAPI.listReferrals(status);
      setRows(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load referrals");
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (row, approve) => {
    const note = approve
      ? window.prompt(`Approve: one free month for ${row.referrer_name} and for ${row.referred_name}. A note (optional):`, "")
      : window.prompt(`Why is ${row.referred_name}'s invitation by ${row.referrer_name} not counted?`, "");
    if (note === null) return;
    if (!approve && !note.trim()) return;

    setBusy(row.id);
    setError("");
    setNotice("");
    try {
      const { data } = approve
        ? await platformAPI.approveReferral(row.id, note.trim() || undefined)
        : await platformAPI.rejectReferral(row.id, note.trim());
      setNotice(
        approve
          ? `Approved. ${row.referrer_name}: ${data.referrer_reward}; ${row.referred_name}: ${data.referred_reward}. Both owners were emailed.`
          : "Rejected.",
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "That did not work");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Referrals</h1>
      <p className="text-sm text-gray-500 mb-6">
        A shop invited through another shop's link. It is ready for you once both shops have paid at least one month;
        approving adds one free month to each (kept for a shop that is not active, added at its next payment).
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatus(key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              status === key ? "bg-primary-600 text-white" : "bg-white text-gray-600 border border-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="text-red-600 mb-4">{error}</p>}
      {notice && <p className="text-green-700 mb-4">{notice}</p>}

      <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
        {rows.length === 0 && <p className="p-6 text-center text-gray-400">Nothing here.</p>}
        {rows.map((row) => (
          <div key={row.id} className="p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="min-w-0 text-sm">
              <p className="font-medium text-gray-900">
                <Link to={`/shops/${row.referrer_shop_id}`} className="text-primary-700 hover:underline">
                  {row.referrer_name}
                </Link>{" "}
                invited{" "}
                <Link to={`/shops/${row.referred_shop_id}`} className="text-primary-700 hover:underline">
                  {row.referred_name}
                </Link>
              </p>
              <p className="text-xs text-gray-500">
                Signed up {new Date(row.created_at).toLocaleDateString()} · inviter {row.referrer_paid ? "has paid" : "not paid yet"} ·
                invited {row.referred_paid ? "has paid" : "not paid yet"}
                {row.qualified_at ? ` · qualified ${new Date(row.qualified_at).toLocaleDateString()}` : ""}
              </p>
              {(row.flags || []).map((flag) => (
                <p key={flag} className="mt-1 text-xs font-semibold text-amber-700">⚠️ {FLAG_LABELS[flag] || flag}</p>
              ))}
              {row.decided_at && (
                <p className="mt-1 text-xs text-gray-500">
                  {row.status === "approved" ? "Approved" : "Rejected"} {new Date(row.decided_at).toLocaleDateString()}
                  {row.decided_by_name ? ` by ${row.decided_by_name}` : ""}
                  {row.decision_note ? ` — ${row.decision_note}` : ""}
                  {row.status === "approved" ? ` (${row.referrer_reward} / ${row.referred_reward})` : ""}
                </p>
              )}
            </div>
            {(row.status === "qualified" || row.status === "signed_up") && (
              <div className="shrink-0 flex gap-3 text-sm">
                {row.status === "qualified" && (
                  <button
                    disabled={busy === row.id}
                    onClick={() => decide(row, true)}
                    className="rounded-lg bg-green-600 px-3 py-1.5 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                )}
                <button
                  disabled={busy === row.id}
                  onClick={() => decide(row, false)}
                  className="text-red-600 hover:underline disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
